import { includes } from 'lodash-es';

import type { Profile } from '@/utils/session/get-profile';

import { EmploymentStatus } from '@/api/core-api/types.gen';
import { DEFAULT_PROFILE_PICTURE_URL } from '@/utils/constants';

export const EMPLOYED_STATUSES = [
  EmploymentStatus.OWNER,
  EmploymentStatus.EMPLOYEDFULLTIME,
  EmploymentStatus.EMPLOYEDPARTTIME,
] as const satisfies readonly EmploymentStatus[];

export function requiresEmployerDetails(
  employmentStatus: EmploymentStatus | undefined,
): employmentStatus is (typeof EMPLOYED_STATUSES)[number] {
  return (
    employmentStatus != null && includes(EMPLOYED_STATUSES, employmentStatus)
  );
}

export type OnboardingStep = 1 | 2 | 3 | 4 | 5;

/**
 * Signals that decide the optional trailing onboarding steps. A participant
 * who already applied to a project (public applications land before
 * onboarding) is prompted for a profile photo; a first-time applicant whose
 * one application is B2B is asked to connect their LinkedIn.
 *
 * `applicationCount` is distinct projects applied to, so `=== 1` means this is
 * their first and only application.
 */
export type OnboardingApplicationSignals = {
  hasAnyApplication: boolean;
  hasB2bApplication: boolean;
  applicationCount: number;
  hasPicture: boolean;
};

/**
 * Whether the participant has a picture of their own. The legacy generic
 * avatar is stored as a real URL on the user, so a truthiness check alone
 * would count it as a photo and silence the prompt — the same exclusion the
 * Avatar component makes when it falls back to initials.
 */
export function hasRealProfilePicture(
  picture: string | null | undefined,
): boolean {
  return !!picture && picture !== DEFAULT_PROFILE_PICTURE_URL;
}

/**
 * The trailing onboarding steps still outstanding for a participant who has
 * already applied: the photo prompt (step 4) until a picture exists, and the
 * connect-LinkedIn step (step 5) until they have connected LinkedIn at least
 * once. These are derived from server state, not from where the participant
 * last was in the flow, so they survive navigating away and coming back — the
 * layout uses this to route a returning participant back into onboarding.
 *
 * Step 5 is deliberately narrow — a first-time applicant (exactly one project
 * applied to) whose single application is B2B, and who has never connected
 * LinkedIn:
 *
 * - `applicationCount === 1` with `hasB2bApplication` means that one
 *   application is the B2B one. Established participants are left alone;
 *   sweeping them in is what turned this prompt into a recurring interruption.
 * - `hasConnectedLinkedIn` is "has ever done LinkedIn", not whether the
 *   connection produced a verified employment record. Anyone who completed the
 *   OAuth flow before is done with this prompt for good, however it turned out.
 */
export function getOutstandingExtraSteps(
  profile: Pick<Profile, 'isPendingLead' | 'hasConnectedLinkedIn'>,
  _signals: OnboardingApplicationSignals,
): OnboardingStep[] {
  if (profile.isPendingLead) {
    return [];
  }

  const steps: OnboardingStep[] = [];

  // PX-475: We probably need a way of specifically identifying that hasB2BApplication only relates to public applications,
  // not normal b2b applications as this sometimes gets triggered by other workflows.

  // if (signals.hasAnyApplication && !signals.hasPicture) {
  //   steps.push(4);
  // }

  // if (
  //   signals.applicationCount === 1 &&
  //   signals.hasB2bApplication &&
  //   !profile.hasConnectedLinkedIn
  // ) {
  //   steps.push(5);
  // }

  return steps;
}

/**
 * Which thanks-for-applying banner onboarding shows, or none.
 *
 * Only first-time applicants see it — exactly one project applied to, which
 * is the public-apply crowd landing here straight off their submission (the
 * same `=== 1` reading as the connect-LinkedIn step above). An established
 * participant re-entering onboarding for a trailing step doesn't need to be
 * thanked for an application from months ago.
 *
 * The variant reveals a positive only: `qualified` when the application
 * passed the screener (manual-review holds included), the neutral `applied`
 * for everything else — disqualified and pending alike, so the banner never
 * confirms a negative.
 */
export function getOnboardingApplicationBanner(signals: {
  applicationCount: number;
  hasQualifiedApplication: boolean;
}): 'qualified' | 'applied' | null {
  if (signals.applicationCount !== 1) {
    return null;
  }
  return signals.hasQualifiedApplication ? 'qualified' : 'applied';
}

/**
 * Which onboarding fields already hold a value on the profile — prefilled by
 * another surface (signup, lead import, legacy portal). Prefilled fields are
 * not surfaced during onboarding; the user is only asked for what is missing.
 */
export type PrefilledOnboardingFields = {
  birthday: boolean;
  gender: boolean;
  city: boolean;
  ethnicity: boolean;
  householdIncome: boolean;
  education: boolean;
  employmentStatus: boolean;
  companyName: boolean;
  workEmail: boolean;
  jobTitle: boolean;
  seniorityLevel: boolean;
};

export function getPrefilledOnboardingFields(
  profile: Pick<Profile, 'demographics' | 'location' | 'employment'>,
): PrefilledOnboardingFields {
  return {
    birthday: !!profile.demographics?.dateOfBirth,
    gender: profile.demographics?.gender != null,
    city: profile.location != null,
    ethnicity: profile.demographics?.ethnicity != null,
    householdIncome: profile.demographics?.householdIncome != null,
    education: profile.demographics?.educationLevel != null,
    employmentStatus: profile.employment?.employmentStatus != null,
    companyName: !!profile.employment?.company?.trim(),
    workEmail: !!profile.employment?.email?.trim(),
    jobTitle: !!profile.employment?.jobTitleId,
    seniorityLevel: profile.employment?.seniorityLevel != null,
  };
}

/**
 * The steps that still have at least one field to ask for. Steps whose fields
 * are all prefilled are skipped entirely. Step 3 is skipped for pending leads
 * (they never see employment questions) and stays visible when a prefilled
 * employed status still lacks a company name, work email or seniority level.
 * Job title is optional, so it alone never keeps step 3 visible.
 *
 * After the form steps, participants who already applied to a project get
 * optional trailing steps: a profile-photo prompt (step 4, any application,
 * skipped when a picture already exists) and a connect-LinkedIn step (step 5,
 * B2B application, skipped once LinkedIn has been connected). The profile is
 * saved when the last form step submits, so the trailing steps sit after
 * onboarding is already complete and losing them (closed tab, failed OAuth)
 * only postpones them — they are re-derived from server state on the next
 * visit.
 *
 * With `onboardingComplete` the participant is only here to finish those
 * trailing steps (a return visit with steps outstanding): the form steps are
 * behind them, so just the extras render — never the forms again.
 */
export function getVisibleOnboardingSteps(
  prefilled: PrefilledOnboardingFields,
  profile: Pick<
    Profile,
    'isPendingLead' | 'employment' | 'hasConnectedLinkedIn'
  >,
  signals: OnboardingApplicationSignals,
  options?: { onboardingComplete?: boolean },
): OnboardingStep[] {
  if (options?.onboardingComplete) {
    return getOutstandingExtraSteps(profile, signals);
  }

  const steps: OnboardingStep[] = [];

  if (!prefilled.birthday || !prefilled.gender || !prefilled.city) {
    steps.push(1);
  }

  if (
    !prefilled.ethnicity ||
    !prefilled.householdIncome ||
    !prefilled.education
  ) {
    steps.push(2);
  }

  if (!profile.isPendingLead) {
    const missingEmployerDetails =
      requiresEmployerDetails(profile.employment?.employmentStatus) &&
      (!prefilled.companyName ||
        !prefilled.workEmail ||
        !prefilled.seniorityLevel);

    if (!prefilled.employmentStatus || missingEmployerDetails) {
      steps.push(3);
    }
  }

  // Unreachable in practice — the route redirects away once step 1's fields
  // are all present — but never return an empty flow.
  const visibleSteps: OnboardingStep[] =
    steps.length > 0 ? steps : [profile.isPendingLead ? 2 : 3];

  return [...visibleSteps, ...getOutstandingExtraSteps(profile, signals)];
}
